import { ENV } from '../../../config';

export interface TemplateSheetSummary {
  sheetName: string;
  role: string;
}

export interface TemplateVersion {
  id: number;
  entityId?: number;
  calcProfileId?: number;
  versionNumber: number;
  fileName: string;
  blobPath: string;
  fileSizeBytes?: number;
  uploadedBy?: string;
  uploadedAt: string;
  isActive: boolean;
  notes?: string;
  downloadUrl?: string;
  standardCalculationCode?: string;
  standardCalculationName?: string;
  sheetsDetected?: TemplateSheetSummary[];
  isValid?: boolean;
}

const BASE = `${ENV.API_URL}/api/template`;

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    tenant: ENV.TENANT_ID ?? '',
  };
}

export const TemplateService = {
  getVersions: (entityId?: number): Promise<TemplateVersion[]> =>
    fetch(`${BASE}${entityId ? `?entityId=${entityId}` : ''}`, {
      headers: authHeaders(),
    }).then(r => r.json()),

  upload: (
    file: File,
    entityId?: number,
    notes?: string,
    standardCalculationCode?: string,
  ): Promise<TemplateVersion> => {
    const form = new FormData();
    form.append('file', file);
    if (entityId) form.append('entityId', String(entityId));
    if (notes) form.append('notes', notes);
    if (standardCalculationCode) form.append('standardCalculationCode', standardCalculationCode);
    return fetch(`${BASE}/upload`, {
      method: 'POST',
      headers: authHeaders(),
      body: form,
    }).then(async r => {
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = body?.detail || body?.ErrorDetail || body?.message || `HTTP ${r.status}`;
        throw Object.assign(new Error(detail), { status: r.status, data: body });
      }
      return body;
    });
  },

  download: (id: number, fileName: string): Promise<void> =>
    fetch(`${BASE}/${id}/download`, { headers: authHeaders() })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      }),

  activate: (id: number): Promise<TemplateVersion> =>
    fetch(`${BASE}/${id}/activate`, {
      method: 'PATCH',
      headers: authHeaders(),
    }).then(r => r.json()),

  delete: (id: number): Promise<void> =>
    fetch(`${BASE}/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(() => {}),

  reassignSc: (versionId: number, standardCalculationCode: string): Promise<TemplateVersion> =>
    fetch(`${BASE}/${versionId}/sc`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ standardCalculationCode }),
    }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    }),
};
